import React, { useEffect, useState } from 'react'

import { Menu, MenuSection } from '@procsea/design-system'

import {
  useCurrentMarketplace,
  useUserSellerCompanyId,
  useUserType,
} from 'src/contexts/userContext'
import { TrackingSource } from 'src/hooks'
import klarysLogo from 'src/images/klarys-logo-menu.png'
import { useMarketplacesQuery } from 'src/queries/shared/marketplaces/marketplaces'
import { useSellerSubscriptionsQuery } from 'src/queries/shared/sellerSubscriptions/sellerSubscriptions'

import useDefaultMarketplaceId from '../../buyer-dashboard/shared/hooks/marketplaces/useDefaultMarketplaceId'
import { InvoicingWithAIButton } from '../../shared/InvoicingWithAIButton/InvoicingWithAIButton'
import { useMenuConfig } from '../useMenuConfig'
import AccountContextMenu from './AccountContextMenu/AccountContextMenu'
import HelpCenterContextMenu from './HelpCenterContextMenu/HelpCenterContextMenu'
import MembershipContextMenu from './MembershipContextMenu/MembershipContextMenu'
import MenuEntryLink from './MenuEntryLink/MenuEntryLink'

const MainMenu = () => {
  const { isSeller } = useUserType()
  const buyerMarketplaceId = useDefaultMarketplaceId()
  const sellerCompanyId = useUserSellerCompanyId()
  const [logo, setLogo] = useState<string | undefined>()

  const { data: marketplaceId, isInitialLoading: isLoadingSellerMarketplaceId } =
    useSellerSubscriptionsQuery({
      queryOptions: {
        enabled: isSeller && !!sellerCompanyId,
        select: sellerSubscriptions =>
          sellerSubscriptions.find(subscription => subscription.canManageEcosystem)?.marketplaceId,
      },
      queryParams: {
        companyId: sellerCompanyId,
      },
    })

  const { data: sellerMarketplace, isInitialLoading: isLoadingSellerMarketplace } =
    useMarketplacesQuery({
      queryOptions: {
        select: marketplaces => marketplaces.find(marketplace => marketplace.id === marketplaceId),
      },
    })

  const buyerMarketplace = useCurrentMarketplace(buyerMarketplaceId)

  const { activeMenuEntry, isMenuReady, menuSections } = useMenuConfig()

  const menuLogo =
    isLoadingSellerMarketplaceId || isLoadingSellerMarketplace
      ? undefined
      : sellerMarketplace?.desktopLogo || buyerMarketplace?.desktopLogo || klarysLogo

  useEffect(() => {
    if (menuLogo) {
      setLogo(menuLogo)
    }
  }, [menuLogo])

  return isMenuReady ? (
    <Menu
      data-e2e="main-menu"
      footer={<AccountContextMenu />}
      logoSrc={logo}
      title={activeMenuEntry}
    >
      <MembershipContextMenu />
      {menuSections.map(({ entries, ...menuSectionProps }) => (
        <MenuSection {...menuSectionProps} key={menuSectionProps['data-e2e']}>
          {entries.map(menuEntryProps => (
            <MenuEntryLink {...menuEntryProps} key={menuEntryProps['data-e2e']} />
          ))}
        </MenuSection>
      ))}

      <InvoicingWithAIButton source={TrackingSource.MAIN_MENU} />

      <HelpCenterContextMenu />
    </Menu>
  ) : null
}

export default MainMenu
